import { useCallback, useState } from 'react';
import { requestAccessToken, verifyAccessToken } from '@/shared/api/auth.api';
import { toApiError } from '@/shared/api/errors';
import { useAuth } from '@/shared/auth/auth-context';

export type LoginStep = 'email' | 'token';

export const TOKEN_LENGTH = 6;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface LoginFlowState {
  step: LoginStep;
  email: string;
  submitting: boolean;
  error: string | null;
}

/**
 * Máquina de estados do login em duas etapas (e-mail → token).
 *
 * Toda a lógica fica aqui para que os componentes de etapa sejam apenas
 * apresentação, e o teste do fluxo não dependa de renderização.
 */
export function useLoginFlow() {
  const { signIn, dismissExpirationNotice } = useAuth();
  const [state, setState] = useState<LoginFlowState>({ step: 'email', email: '', submitting: false, error: null });

  const submitEmail = useCallback(
    async (rawEmail: string) => {
      const email = rawEmail.trim();

      if (!EMAIL_PATTERN.test(email)) {
        setState(current => ({ ...current, error: 'Informe um e-mail válido.' }));
        return;
      }

      dismissExpirationNotice();
      setState(current => ({ ...current, submitting: true, error: null }));

      try {
        await requestAccessToken(email);
        setState({ step: 'token', email, submitting: false, error: null });
      } catch (error) {
        const apiError = toApiError(error);
        setState(current => ({
          ...current,
          submitting: false,
          error: apiError.kind === 'notFound' ? 'E-mail não autorizado a acessar o painel.' : apiError.message,
        }));
      }
    },
    [dismissExpirationNotice],
  );

  /** Devolve se o código passou: a etapa limpa o campo quando não passou. */
  const submitToken = useCallback(
    async (token: string): Promise<boolean> => {
      if (token.length !== TOKEN_LENGTH) {
        setState(current => ({ ...current, error: `O código tem ${TOKEN_LENGTH} dígitos.` }));
        return false;
      }

      setState(current => ({ ...current, submitting: true, error: null }));

      try {
        // O nome do operador vem da validação do código, e entra na sessão: é o que
        // o cabeçalho mostra acima do e-mail. Pode voltar `null` — a exibição tem reserva.
        const name = await verifyAccessToken(state.email, token);
        // `signIn` troca a tela; não há set de estado depois disso.
        signIn({ email: state.email, token, ...(name ? { name } : {}) });
        return true;
      } catch (error) {
        const apiError = toApiError(error);
        setState(current => ({
          ...current,
          submitting: false,
          error: apiError.kind === 'validation' ? 'Código inválido ou expirado. Solicite um novo.' : apiError.message,
        }));
        return false;
      }
    },
    [signIn, state.email],
  );

  const resendToken = useCallback(async () => {
    setState(current => ({ ...current, submitting: true, error: null }));
    try {
      await requestAccessToken(state.email);
      setState(current => ({ ...current, submitting: false, error: null }));
      return true;
    } catch (error) {
      setState(current => ({ ...current, submitting: false, error: toApiError(error).message }));
      return false;
    }
  }, [state.email]);

  const backToEmail = useCallback(() => {
    setState({ step: 'email', email: '', submitting: false, error: null });
  }, []);

  const clearError = useCallback(() => {
    setState(current => (current.error === null ? current : { ...current, error: null }));
  }, []);

  return { ...state, submitEmail, submitToken, resendToken, backToEmail, clearError };
}
